import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from "@angular/common/http";
import { Router } from "@angular/router";
import { LocalStorage } from "#services/local-storage.service";
import { Observable, throwError } from "rxjs";
import { tap, catchError } from "rxjs/operators";

import { Store } from "@ngrx/store";
import * as fromStore from "#login/store";
import { SnackBarService } from "#services/snackbar.service";
import { CookieService } from "#core/services/cookie.service";

/**
 * HTTP Requests
 * After login, the authentication token needs to be added to every api request to the passport api.
 * If key exists in local storage, adds the header, else continue
 *
 * HTTP Responses
 * If a request to the api is returns a 503 or 403, redirect the user
 *
 * source:
 * https://medium.com/@ryanchenkie_40935/angular-authentication-using-the-http-client-and-http-interceptors-2f9d1540eb8
 */
@Injectable({
    providedIn: "root",
})
export class AuthHttpInterceptor implements HttpInterceptor {
    constructor(
        private localStorage: LocalStorage,
        private store: Store<fromStore.UserState>,
        private router: Router,
        private snackbarService: SnackBarService,
        private cookieService: CookieService
    ) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        const API_ROOT = (<any>window).apiHost;
        // const CL_APIS_URI = (<any>window).CL_APIS_URI;

        if ((request.url.search(API_ROOT) === 0/* || request.url.search(CL_APIS_URI) === 0 */)) {
            // request to passport api
            let key: string = this.cookieService.get("CL_TOKEN2");
            // Due to parent and local domain cookies conflicts, on logout a cloned 'CL_TOKEN2 is set to the local domain for path
            // need to clear that out if it exist and try getting from the parent `.<cl domain>.com` vs '<local>.<cl domain>.com'
            if (key === undefined || key === null || key === "") {
                this.cookieService.delete("CL_TOKEN2");
                key = this.cookieService.get("CL_TOKEN2"); // qerk with local vs wildcard domain paths for cookies
            }
            if (key) {
                request = request.clone({
                    headers: request.headers.set("Authorization", `Bearer ${key}`),
                });
            }
        }
        return next.handle(request).pipe(
            tap(
                (event) => {},
                (response) => {
                    if (
                        response.name === "HttpErrorResponse" &&
                        (response.url === null ||
                            response.url.search(API_ROOT) === 0) &&
                        (response.status === 503 || response.status === 403)
                    ) {
                        // Clear out the store
                        this.store.dispatch(fromStore.userLogoutMetaReducer());
                        // handle error cases
                        if (response.status === 503) {
                            // Passport Server Unavailable (mainly due to maintenance)
                            this.store.dispatch(fromStore.http503());
                        }
                        if (response.status === 403) {
                            // Passport Server Authorized (invalid token)
                            this.store.dispatch(fromStore.http403());
                        }

                        const logoutRedirectURL =
                            this.localStorage.get("logoutRedirectURL");
                        this.localStorage.clear();

                        // Must be after the clear, url needs to persist
                        if (
                            ((<any>window).isDemo ||
                                (<any>window).env === "s" ||
                                (<any>window).env === "local") &&
                            logoutRedirectURL !== undefined &&
                            logoutRedirectURL !== null &&
                            logoutRedirectURL !== "null" &&
                            logoutRedirectURL !== ""
                        ) {
                            this.localStorage.set(
                                "logoutRedirectURL",
                                logoutRedirectURL
                            );
                        }

                        this.router.navigate(["/login"]);
                    } else if (response.status === 422) {
                        // Generally validations errors shouldn't be shown to users but it does make debugging easier
                        // keeping the code here for debuging use
                        // if (response.error && response.error.message) {
                        //     this.snackbarService.error(response.error.message);
                        // } else if (
                        //     response.error &&
                        //     response.error[
                        //         "Request failed validation with the following errors: "
                        //     ]
                        // ) {
                        //     const errors =
                        //         response.error[
                        //             "Request failed validation with the following errors: "
                        //         ];
                        //     this.snackbarService.error(errors.join(", "));
                        // }
                    }
                    // console.log("interceptor response: ", response);
                }
            ),
            catchError((error, caught) => {
                if (
                    error.status !== 422 &&
                    error.status !== 404 &&
                    error.status !== 413 &&
                    error.status !== 494 &&
                    error.status !== 431 &&
                    error.status !== 415 &&
                    error.status !== 200
                ) {
                    // Don't push 422 errors up to the snackbar, catch them and send them on to the calling service.
                    // intercept the response error and displace it to the console
                    let message = "";
                    if (error.error && typeof error.error === "string") {
                        message = error.error + " ";
                    }
                    if (error.error && error.error.message) {
                        message += error.error.message;
                    } else if (error.message) {
                        message += error.message;
                    } else {
                        message += "Unrecoverable Error";
                    }
                    // PAS-12087 disable snackbar notification in favor of console error
                    // logs due to retries creating too much noise
                    // this.snackbarService.error(message);
                    console.error(message);
                }

                // return the error to the method that called it
                return throwError(error);
            })
        );
    }
}
